// Phase 5Z.11 — Driver calendar (own schedule + recorded time entries)
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Check, Calendar as CalIcon, Clock, X, Pencil, AlertCircle, Send } from "lucide-react";
import { fahrerApi } from "./lib/api";
import { toast } from "sonner";
import { fmtDateTime, fmtDate } from "@/lib/format";

const SHIFT_COLORS = {
  arbeit:     "bg-emerald-500",
  homeoffice: "bg-blue-500",
  urlaub:     "bg-amber-500",
  frei:       "bg-zinc-700",
  krank:      "bg-rose-500",
};

function fmtMin(min) {
  if (!min) return "0h 0m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export default function FahrerCalendar() {
  const { t, i18n } = useTranslation();
  const [ym, setYm] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null); // for the detail modal

  useEffect(() => {
    setData(null);
    fahrerApi.get(`/fahrer/schedule?ym=${ym}`).then(({ data }) => setData(data));
  }, [ym]);

  const grid = useMemo(() => {
    if (!data) return null;
    const [y, m] = ym.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const startWeekday = (first.getDay() + 6) % 7; // Mon = 0
    const days = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const shift = data.shifts.find(s => s.date === iso);
      const entry = data.time_entries.find(e => e.date === iso);
      days.push({ d, iso, shift, entry });
    }
    return days;
  }, [data, ym]);

  const monthLabel = useMemo(() => {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(i18n.resolvedLanguage, { month: "long", year: "numeric" });
  }, [ym, i18n.resolvedLanguage]);

  function step(delta) {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }

  return (
    <div className="space-y-4" data-testid="fahrer-calendar-page">
      <div>
        <h1 className="text-xl font-bold text-zinc-50">{t("zeit.calendar_title")}</h1>
        <p className="text-xs text-zinc-500 mt-0.5">{t("nav.schedule")}</p>
      </div>

      {/* Phase 5Z.24 — Monthly summary stats */}
      {data?.summary && (
        <div className="grid grid-cols-2 gap-2" data-testid="fahrer-cal-summary">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-semibold">Arbeitszeit</div>
            <div className="text-2xl font-bold text-emerald-100 mt-0.5 tabular-nums" data-testid="fahrer-cal-total-work">
              {fmtMin(data.summary.total_work_minutes)}
            </div>
            <div className="text-[10px] text-emerald-300/60 mt-0.5">{data.summary.entries_count} Erfassungen</div>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-blue-300/80 font-semibold">Lenkzeit</div>
            <div className="text-2xl font-bold text-blue-100 mt-0.5 tabular-nums" data-testid="fahrer-cal-total-drive">
              {fmtMin(data.summary.total_driving_minutes)}
            </div>
            <div className="text-[10px] text-blue-300/60 mt-0.5">{data.summary.total_km.toLocaleString()} km gefahren</div>
          </div>
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => step(-1)} data-testid="fahrer-cal-prev" className="p-2 hover:bg-zinc-800 rounded">
            <ChevronLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <h3 className="text-sm font-semibold text-zinc-100 capitalize" data-testid="fahrer-cal-month-label">{monthLabel}</h3>
          <button onClick={() => step(1)} data-testid="fahrer-cal-next" className="p-2 hover:bg-zinc-800 rounded">
            <ChevronRight className="w-4 h-4 text-zinc-400" />
          </button>
        </div>

        {/* Header row */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map(d => (
            <div key={d} className="text-[10px] text-center text-zinc-500 font-semibold">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        {grid === null ? (
          <div className="text-center text-zinc-500 text-sm py-8">{t("app.loading")}</div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {grid.map((cell, i) => (
              <DayCell key={i} cell={cell} onClick={(c) => c?.entry && setSelectedEntry(c.entry)} />
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <Legend color="bg-emerald-500" label={t("zeit.calendar.legend_arbeit")} />
          <Legend color="bg-zinc-700" label={t("zeit.calendar.legend_frei")} />
          <Legend color="bg-amber-500" label={t("zeit.calendar.legend_urlaub")} />
          <Legend color="bg-blue-500" label={t("zeit.calendar.legend_homeoffice")} />
        </div>
        <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center gap-1.5 text-[10px] text-zinc-400">
          <Check className="w-3 h-3 text-amber-400" />
          {t("zeit.calendar.legend_done")}
        </div>
      </div>

      {/* Recent entries list */}
      <div>
        <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Letzte Erfassungen</h3>
        {data && data.time_entries.length === 0 ? (
          <div className="text-zinc-500 text-xs italic text-center py-4">{t("zeit.calendar.no_entries")}</div>
        ) : (
          <div className="space-y-1.5">
            {(data?.time_entries || []).slice(0, 8).map((e) => (
              <button
                type="button"
                key={e.id}
                onClick={() => setSelectedEntry(e)}
                className="w-full bg-zinc-900 border border-zinc-800 rounded p-2.5 flex items-center gap-2.5 hover:border-amber-600 transition-colors text-left"
                data-testid={`fahrer-cal-entry-${e.date}`}
              >
                <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono text-zinc-100">{fmtDate(e.date)}</div>
                  <div className="text-[10px] text-zinc-500">{e.work_start}–{e.work_end} · {(e.end_km - e.start_km)} km</div>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
                  e.status === "freigegeben" ? "bg-emerald-500/15 text-emerald-300" :
                  e.status === "geprueft" ? "bg-blue-500/15 text-blue-300" :
                  "bg-zinc-800 text-zinc-400"
                }`}>
                  {e.status === "freigegeben" ? "✓" : e.status === "geprueft" ? "⏳" : "○"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Phase 5Z.24 — Day detail modal */}
      {selectedEntry && (
        <DayDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      )}
    </div>
  );
}

function DayDetailModal({ entry, onClose }) {
  const [full, setFull] = useState(null);
  const [err, setErr] = useState(null);
  const [requests, setRequests] = useState([]); // own change-requests for this entry
  const [showForm, setShowForm] = useState(false);

  async function reloadAll() {
    try {
      const { data } = await fahrerApi.get(`/fahrer/zeiterfassung/${entry.id}`);
      setFull(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler beim Laden");
    }
    try {
      const { data: list } = await fahrerApi.get(`/fahrer/zeit-change-requests`);
      setRequests((list || []).filter((r) => r.time_entry_id === entry.id));
    } catch { /* ignore */ }
  }

  useEffect(() => {
    let cancelled = false;
    fahrerApi.get(`/fahrer/zeiterfassung/${entry.id}`)
      .then(({ data }) => { if (!cancelled) setFull(data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Fehler beim Laden"); });
    fahrerApi.get(`/fahrer/zeit-change-requests`)
      .then(({ data }) => {
        if (cancelled) return;
        setRequests((data || []).filter((r) => r.time_entry_id === entry.id));
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [entry.id]);

  const t = full || entry;
  const km = (t.end_km || 0) - (t.start_km || 0);
  const pendingReq = requests.find((r) => r.status === "pending");
  const lastRejected = requests.find((r) => r.status === "rejected");

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="fahrer-day-detail-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-t-xl sm:rounded-xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-zinc-100">Tagesdetail</div>
            <div className="text-xs text-zinc-400 font-mono">{fmtDate(t.date)}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 rounded" data-testid="fahrer-detail-close">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {err && (
            <div className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 text-xs text-red-300">
              {err}
            </div>
          )}

          {full?.was_edited_by_management && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 flex gap-2 items-start" data-testid="fahrer-edited-banner">
              <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-semibold text-amber-300">Eintrag wurde von der Verwaltung bearbeitet</div>
                <div className="text-[10px] text-amber-200/80 mt-0.5">Siehe Änderungs-Verlauf unten</div>
              </div>
            </div>
          )}

          {/* Time block */}
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Arbeit Start" value={t.work_start || "—"} />
            <Stat label="Arbeit Ende" value={t.work_end || "—"} />
            <Stat label="Fahrt Start" value={t.driving_start || "—"} />
            <Stat label="Fahrt Ende" value={t.driving_end || "—"} />
          </div>

          {/* KM block */}
          <div className="grid grid-cols-2 gap-2">
            <Stat label="KM Start" value={(t.start_km || 0).toLocaleString()} mono />
            <Stat label="KM Ende" value={(t.end_km || 0).toLocaleString()} mono />
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300/80">Gefahren</div>
            <div className="text-xl font-bold text-emerald-100 tabular-nums" data-testid="fahrer-detail-km">
              {km.toLocaleString()} km
            </div>
          </div>

          {/* Hours */}
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Arbeit" value={fmtMin(t.work_minutes || 0)} testid="fahrer-detail-work" />
            <Stat label="Lenkzeit" value={fmtMin(t.driving_minutes || 0)} testid="fahrer-detail-drive" />
            <Stat label="Pause" value={fmtMin(t.break_minutes || 0)} testid="fahrer-detail-break" />
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400">Status:</span>
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider ${
              t.status === "freigegeben" ? "bg-emerald-500/15 text-emerald-300" :
              t.status === "geprueft" ? "bg-blue-500/15 text-blue-300" :
              t.status === "abgelehnt" ? "bg-red-500/15 text-red-300" :
              "bg-zinc-800 text-zinc-400"
            }`}>
              {t.status || "offen"}
            </span>
          </div>

          {/* Workplace + notes */}
          {(t.workplace || t.notes) && (
            <div className="space-y-1.5">
              {t.workplace && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Einsatzort</div>
                  <div className="text-sm text-zinc-200">{t.workplace}</div>
                </div>
              )}
              {t.notes && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Notizen</div>
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap">{t.notes}</div>
                </div>
              )}
            </div>
          )}

          {/* Phase 5Z.29 — Change-request section */}
          <EditRequestSection
            entry={t}
            pending={pendingReq}
            rejected={lastRejected}
            showForm={showForm}
            onOpenForm={() => setShowForm(true)}
            onCancelForm={() => setShowForm(false)}
            onSubmitted={() => { setShowForm(false); reloadAll(); }}
          />

          {/* Edit history */}
          {full?.edit_history && full.edit_history.length > 0 && (
            <div className="border-t border-zinc-800 pt-3" data-testid="fahrer-detail-history">
              <div className="flex items-center gap-1.5 mb-2">
                <Pencil className="w-3.5 h-3.5 text-amber-400" />
                <h4 className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Änderungs-Verlauf</h4>
              </div>
              <div className="space-y-2">
                {full.edit_history.map((h, idx) => (
                  <div key={idx} className="bg-zinc-900 border border-zinc-800 rounded p-2.5" data-testid={`fahrer-history-${idx}`}>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-zinc-200 font-medium">{h.actor_email}</span>
                      <span className="text-zinc-500 font-mono">{fmtDateTime(h.timestamp)}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      Rolle: <span className="text-zinc-300 capitalize">{h.actor_role || "—"}</span> · Aktion: <span className="text-zinc-300">{h.action}</span>
                    </div>
                    {h.changes && h.changes.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {h.changes.map((c, ci) => (
                          <li key={ci} className="text-[10px] text-zinc-300">
                            <span className="text-zinc-500">{c.field}:</span>{" "}
                            <span className="text-red-400 line-through">{c.before}</span>{" "}
                            <span className="text-zinc-500">→</span>{" "}
                            <span className="text-emerald-300">{c.after}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono, testid }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm text-zinc-100 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function DayCell({ cell, onClick }) {
  if (!cell) return <div className="aspect-square" />;
  const today = new Date().toISOString().slice(0, 10);
  const isToday = cell.iso === today;
  const stype = cell.shift?.shift_type;
  const colorBg = SHIFT_COLORS[stype] || "bg-zinc-800";
  const hasEntry = !!cell.entry;
  return (
    <button
      type="button"
      onClick={() => onClick && onClick(cell)}
      disabled={!hasEntry}
      className={`relative aspect-square rounded ${stype ? "border border-zinc-700" : ""} ${isToday ? "ring-2 ring-amber-500" : ""} bg-zinc-900 flex flex-col items-center justify-center ${hasEntry ? "hover:bg-zinc-800 cursor-pointer" : ""} disabled:cursor-default transition-colors`}
      data-testid={`fahrer-cal-day-${cell.iso}`}
    >
      <div className="text-xs font-medium text-zinc-200">{cell.d}</div>
      {stype && (
        <div className={`mt-0.5 w-2 h-2 rounded-full ${colorBg}`} />
      )}
      {hasEntry && (
        <div className="absolute top-0.5 right-0.5 w-3 h-3 rounded-full bg-amber-500 flex items-center justify-center">
          <Check className="w-2 h-2 text-white" strokeWidth={3} />
        </div>
      )}
    </button>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-zinc-300">{label}</span>
    </div>
  );
}

// Phase 5Z.29 — Driver edit-request form & status display
const EDITABLE_FIELDS = [
  { key: "work_start", label: "Arbeit Start", type: "time" },
  { key: "work_end", label: "Arbeit Ende", type: "time" },
  { key: "driving_start", label: "Fahrt Start", type: "time" },
  { key: "driving_end", label: "Fahrt Ende", type: "time" },
  { key: "start_km", label: "KM Start", type: "number" },
  { key: "end_km", label: "KM Ende", type: "number" },
  { key: "workplace", label: "Einsatzort", type: "text" },
  { key: "notes", label: "Notizen", type: "textarea" },
];

function EditRequestSection({ entry, pending, rejected, showForm, onOpenForm, onCancelForm, onSubmitted }) {
  if (pending) {
    return (
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 space-y-2" data-testid="fahrer-er-pending">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          <span className="text-xs font-semibold text-blue-300">Änderungs-Anfrage offen</span>
        </div>
        <div className="text-[11px] text-blue-200/80">
          Eingereicht {fmtDateTime(pending.created_at)} — wartet auf Freigabe der Verwaltung.
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 space-y-1">
          {Object.entries(pending.proposed_changes || {}).map(([k, v]) => {
            const lbl = (EDITABLE_FIELDS.find((f) => f.key === k)?.label) || k;
            return (
              <div key={k} className="text-[11px] text-zinc-300 flex items-center gap-1.5">
                <span className="text-zinc-500">{lbl}:</span>
                <span className="text-red-400 line-through">{String(pending.before?.[k] ?? "—")}</span>
                <span className="text-zinc-500">→</span>
                <span className="text-emerald-300">{String(v ?? "—")}</span>
              </div>
            );
          })}
        </div>
        {pending.reason && (
          <div className="text-[11px] text-zinc-400 italic border-l-2 border-blue-500/50 pl-2">
            „{pending.reason}"
          </div>
        )}
      </div>
    );
  }
  if (showForm) {
    return (
      <EditRequestForm
        entry={entry}
        onCancel={onCancelForm}
        onSubmitted={onSubmitted}
      />
    );
  }
  return (
    <div className="border-t border-zinc-800 pt-3 space-y-2">
      {rejected && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5" data-testid="fahrer-er-rejected">
          <div className="flex items-center gap-1.5 mb-1">
            <X className="w-3.5 h-3.5 text-red-400" />
            <span className="text-[11px] font-semibold text-red-300">Letzte Anfrage abgelehnt</span>
          </div>
          {rejected.rejection_reason && (
            <div className="text-[10px] text-red-200/80">Grund: {rejected.rejection_reason}</div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={onOpenForm}
        data-testid="fahrer-er-open-form"
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 transition-colors text-xs font-semibold"
      >
        <Pencil className="w-3.5 h-3.5" />
        Änderung beantragen
      </button>
      <div className="text-[10px] text-zinc-500 text-center">
        Schlage Korrekturen vor — die Verwaltung muss freigeben.
      </div>
    </div>
  );
}

function EditRequestForm({ entry, onCancel, onSubmitted }) {
  const [proposed, setProposed] = useState(() => {
    const init = {};
    for (const f of EDITABLE_FIELDS) init[f.key] = entry[f.key] ?? "";
    return init;
  });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function setField(k, v) {
    setProposed((p) => ({ ...p, [k]: v }));
  }

  async function submit() {
    setErr(null);
    // Compute diff vs current entry — only send actually changed fields
    const diff = {};
    for (const f of EDITABLE_FIELDS) {
      const cur = entry[f.key] ?? "";
      let nv = proposed[f.key];
      if (f.type === "number") nv = nv === "" || nv === null ? null : Number(nv);
      if (f.type === "text" || f.type === "time" || f.type === "textarea") nv = (nv ?? "").toString().trim();
      const curN = f.type === "number" ? (cur === "" || cur === null ? null : Number(cur)) : (cur ?? "").toString().trim();
      if (nv !== curN) diff[f.key] = nv;
    }
    if (Object.keys(diff).length === 0) {
      setErr("Bitte mindestens ein Feld ändern.");
      return;
    }
    if (!reason.trim() || reason.trim().length < 3) {
      setErr("Bitte gib eine kurze Begründung ein.");
      return;
    }
    setBusy(true);
    try {
      await fahrerApi.post(`/fahrer/zeiterfassung/${entry.id}/edit-request`, {
        proposed_changes: diff,
        reason: reason.trim(),
      });
      toast.success("Änderungs-Anfrage gesendet");
      onSubmitted?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-zinc-800 pt-3 space-y-3" data-testid="fahrer-er-form">
      <div className="flex items-center gap-1.5">
        <Pencil className="w-3.5 h-3.5 text-amber-400" />
        <h4 className="text-xs uppercase tracking-wider text-amber-300 font-semibold">Änderung beantragen</h4>
      </div>

      <div className="space-y-2">
        {EDITABLE_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea
                value={proposed[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                rows={2}
                data-testid={`fahrer-er-input-${f.key}`}
                className="w-full mt-0.5 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 focus:border-amber-500 focus:outline-none"
              />
            ) : (
              <input
                type={f.type === "number" ? "number" : f.type === "time" ? "time" : "text"}
                value={proposed[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                data-testid={`fahrer-er-input-${f.key}`}
                className="w-full mt-0.5 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 font-mono focus:border-amber-500 focus:outline-none"
              />
            )}
          </div>
        ))}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Begründung *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="z.B. Lenkbeginn habe ich um 06:30 nicht 07:00 gestartet"
            data-testid="fahrer-er-reason"
            className="w-full mt-0.5 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-100 focus:border-amber-500 focus:outline-none"
          />
        </div>
      </div>

      {err && (
        <div className="bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5 text-[11px] text-red-300" data-testid="fahrer-er-error">
          {err}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="fahrer-er-cancel"
          className="flex-1 px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-semibold disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid="fahrer-er-submit"
          className="flex-1 px-3 py-2 rounded bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <Send className="w-3.5 h-3.5" />
          {busy ? "Sende..." : "Senden"}
        </button>
      </div>
    </div>
  );
}
